import React, { useState, useEffect } from 'react';
import { X, Package, MapPin, CheckCircle, AlertCircle, ArrowRight, Loader2, Search, BarChart3 } from 'lucide-react';
import { StcTransDto, stcTransService, CreateStcTransRequest } from '../../../../services/stcTransService';
import { putAwayService, CreatePutAwayRequest, PutAwayReservationDto } from '../../../../services/putAwayService';
import { directReceiptService, DirectReceiptRequest } from '../../../../services/directReceiptService';
import { itemsService } from '../../../../services/itemsService';
import { stcBinService } from '../../../../services/stcBinService';
import { ItemsDto } from '../../../../types/items';
import { StcBinDto } from '../../../../types/stcBin';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface DirectReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  stcTrans: StcTransDto;
  companyNo: string;
  warehouseID: string;
  onSuccess?: () => void;
}

interface PutAwaySettings {
  binGrpNo: number;
  pickRuleNo: number;
  putAwayToPickableBinYesNo: boolean;
  quantity: number;
}

interface PlacementRecord {
  location: string;
  quantity: number;
}

export const DirectReceiptModal: React.FC<DirectReceiptModalProps> = ({
  isOpen,
  onClose,
  stcTrans,
  companyNo,
  warehouseID,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingBins, setLoadingBins] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [itemData, setItemData] = useState<ItemsDto | null>(null);
  const [locationSuggestions, setLocationSuggestions] = useState<PutAwayReservationDto[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [customLocation, setCustomLocation] = useState<string>('');
  const [useCustomLocation, setUseCustomLocation] = useState(false);
  const [availableBins, setAvailableBins] = useState<StcBinDto[]>([]);
  const [binSearchTerm, setBinSearchTerm] = useState<string>('');
  const [editableData, setEditableData] = useState({
    quantity: stcTrans.quantity || 0,
    itemBatchNo: stcTrans.itemBatchNo || '',
    itemBatchExpDate: stcTrans.itemBatchExpDate 
      ? new Date(stcTrans.itemBatchExpDate).toISOString().split('T')[0] 
      : ''
  });
  const [settings, setSettings] = useState<PutAwaySettings>({
    binGrpNo: 1,
    pickRuleNo: 1,
    putAwayToPickableBinYesNo: true,
    quantity: stcTrans.quantity || 0
  });

  // Split placement tracking
  const [currentStcTrans, setCurrentStcTrans] = useState<StcTransDto>(stcTrans);
  const [originalTotalQuantity] = useState<number>(stcTrans.quantity || 0);
  const [remainingQuantity, setRemainingQuantity] = useState<number>(stcTrans.quantity || 0);
  const [totalPlaced, setTotalPlaced] = useState<number>(0);
  const [completedPlacements, setCompletedPlacements] = useState<PlacementRecord[]>([]);
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [isProcessingSplit, setIsProcessingSplit] = useState<boolean>(false);

  // Load item data and bins when modal opens
  useEffect(() => {
    if (isOpen && stcTrans.itemNo) {
      loadItemData();
      loadAvailableBins();
    }
  }, [isOpen, stcTrans.itemNo, companyNo, warehouseID]);

  // Load location suggestions when item data or editable data changes
  useEffect(() => {
    if (itemData && editableData.quantity > 0) {
      loadLocationSuggestions();
    }
  }, [itemData, editableData.quantity]);

  const loadAvailableBins = async () => {
    try {
      setLoadingBins(true);
      setError(null);
      
      const bins = await stcBinService.getAvailableBinsForReceipt(companyNo, warehouseID);
      setAvailableBins(bins);
      
    } catch (err) {
      console.error('Error loading available bins:', err);
      setError(t("warehouse.modules.receipt.directReceipt.errors.loadLocationsFailed", { error: err instanceof Error ? err.message : t("common.errors.unknownError") }));
    } finally {
      setLoadingBins(false);
    }
  };

  const loadItemData = async () => {
    try {
      setError(null);

      const item = await itemsService.getItemByCompanyAndItemNo(companyNo, stcTrans.itemNo);
      setItemData(item);

      // Update settings and editable data with item data
      setSettings(prev => ({
        ...prev,
        binGrpNo: item.binGrpNo || 1,
        pickRuleNo: item.pickRuleNo || 1,
        putAwayToPickableBinYesNo: item.putAwayToPickableBinYesNo ?? true
      }));

      setEditableData(prev => ({
        ...prev,
        quantity: stcTrans.quantity || 0
      }));

    } catch (err) {
      console.error('Error loading item data:', err);
      // Don't show error to user - just use default values and continue
      // This can happen if item exists in StcTrans but not in Items table
      setSettings(prev => ({
        ...prev,
        binGrpNo: 1,
        pickRuleNo: 1,
        putAwayToPickableBinYesNo: true
      }));

      setEditableData(prev => ({
        ...prev,
        quantity: stcTrans.quantity || 0
      }));
    }
  };

  const loadLocationSuggestions = async () => {
    if (!currentStcTrans.itemNo || editableData.quantity <= 0) {
      return;
    }

    try {
      setLoadingLocations(true);
      setError(null);

      const request: CreatePutAwayRequest = {
        companyNo,
        warehouseID,
        itemNo: currentStcTrans.itemNo,
        binGrpNo: settings.binGrpNo,
        pickRuleNo: settings.pickRuleNo,
        grShipmentNo: currentStcTrans.grShipmentNo || 0,
        putAwayToPickableBinYesNo: settings.putAwayToPickableBinYesNo,
        quantity: editableData.quantity,
        // Include POrder info for Standard mode
        pOrderNo: currentStcTrans.orderNo,
        pOrderLineNo: currentStcTrans.lineNo
      };

      const response = await putAwayService.createPutAwayForStcIntRelOrderLine(request);
      
      if (response.success) {
        setLocationSuggestions(response.reservations);
        // Auto-select only if exactly one suggestion (single location = original behavior)
        if (response.reservations.length === 1) {
          setSelectedLocation(response.reservations[0].binCode);
        }
      } else {
        setError(response.errorMessage || t("warehouse.modules.receipt.directReceipt.errors.loadLocationSuggestionsFailed"));
      }
    } catch (err) {
      console.error('Error loading location suggestions:', err);
      setError(t("warehouse.modules.receipt.directReceipt.errors.loadLocationSuggestionsError", { error: err instanceof Error ? err.message : t("common.errors.unknownError") }));
    } finally {
      setLoadingLocations(false);
    }
  };

  const isRequiredFieldsMissing = () => {
    const itemType = currentStcTrans.itemType;

    // Check batch number for ItemType 1/5 (required), 4 (optional - auto-generated)
    if ((itemType === 1 || itemType === 5) && !editableData.itemBatchNo?.trim()) {
      return true;
    }

    // Check expiry date for ItemType 4 (required)
    if (itemType === 4 && !editableData.itemBatchExpDate?.trim()) {
      return true;
    }

    return false;
  };

  const handleDirectReceipt = async () => {
    const finalLocation = useCustomLocation ? customLocation : selectedLocation;

    if (!finalLocation.trim()) {
      setError(t("warehouse.modules.receipt.directReceipt.errors.selectLocation"));
      return;
    }

    // Validation: ItemType-based batch/expiry validation
    const itemType = currentStcTrans.itemType;

    // Validate batch number for ItemType 1/5 (required), 4 (optional)
    if ((itemType === 1 || itemType === 5) && !editableData.itemBatchNo?.trim()) {
      setError(t("warehouse.modules.receipt.directReceipt.errors.batchRequired"));
      return;
    }

    // Validate expiry date for ItemType 4 (required)
    if (itemType === 4 && !editableData.itemBatchExpDate?.trim()) {
      setError(t("warehouse.modules.receipt.directReceipt.errors.expiryDateRequired"));
      return;
    }

    // Prevent double-click
    if (isProcessingSplit) return;

    try {
      setIsProcessingSplit(true);
      setLoading(true);
      setError(null);

      const placementQty = editableData.quantity;

      // Call the direct receipt service on the current StcTrans
      const directReceiptRequest: DirectReceiptRequest = {
        locationCode: finalLocation.trim(),
        quantity: placementQty,
        itemBatchNo: editableData.itemBatchNo || undefined,
        itemBatchExpDate: editableData.itemBatchExpDate
          ? editableData.itemBatchExpDate
          : undefined,
        notes: `Direct receipt processed with ${useCustomLocation ? 'custom' : 'suggested'} location`,
        processedBy: 'Current User'
      };

      const response = await directReceiptService.processDirectReceipt(
        currentStcTrans.batchID,
        currentStcTrans.lineID,
        directReceiptRequest
      );

      if (response.success) {
        console.log('Direct receipt processed successfully:', response);

        // Register placement
        const newPlacement: PlacementRecord = { location: finalLocation.trim(), quantity: placementQty };
        const updatedPlacements = [...completedPlacements, newPlacement];
        setCompletedPlacements(updatedPlacements);

        const newTotalPlaced = totalPlaced + placementQty;
        setTotalPlaced(newTotalPlaced);

        const newRemaining = originalTotalQuantity - newTotalPlaced;
        setRemainingQuantity(newRemaining);

        if (newRemaining <= 0) {
          // All placed - show summary (don't call onSuccess yet - wait for user to close summary)
          setIsComplete(true);
        } else {
          // More to place - create new StcTrans for remaining quantity
          try {
            const createRequest: CreateStcTransRequest = {
              batchID: currentStcTrans.batchID,
              lineType: currentStcTrans.lineType,
              orderNo: currentStcTrans.orderNo,
              lineNo: currentStcTrans.lineNo,
              locationName: currentStcTrans.locationName,
              quantity: newRemaining,
              warehouse: currentStcTrans.warehouse,
              operatorID: currentStcTrans.operatorID,
              profileID: currentStcTrans.profileID,
              itemNo: currentStcTrans.itemNo,
              itemUnitCode: currentStcTrans.itemUnitCode,
              itemBarCode: currentStcTrans.itemBarCode,
              description1: currentStcTrans.description1,
              itemBatchNo: editableData.itemBatchNo || currentStcTrans.itemBatchNo,
              itemBatchExpDate: editableData.itemBatchExpDate
                ? new Date(editableData.itemBatchExpDate)
                : currentStcTrans.itemBatchExpDate,
              itemSerialNo: currentStcTrans.itemSerialNo,
              quantityOrigin: newRemaining,
              itemUnitTxt: currentStcTrans.itemUnitTxt,
              lineTypeOrigin: currentStcTrans.lineTypeOrigin,
              transType: currentStcTrans.transType,
              grShipmentNo: currentStcTrans.grShipmentNo,
              itemType: currentStcTrans.itemType,
              orderNoERP: currentStcTrans.orderNoERP,
              lineNoERP: currentStcTrans.lineNoERP,
            };

            const newStcTrans = await stcTransService.createTransaction(
              currentStcTrans.batchID,
              createRequest
            );

            // Update state for next placement round
            setCurrentStcTrans(newStcTrans);
            setEditableData(prev => ({
              ...prev,
              quantity: newRemaining
            }));
            setSelectedLocation('');
            setCustomLocation('');
            setUseCustomLocation(false);
            setLocationSuggestions([]);

            // Reload location suggestions for new remaining quantity
            // (will trigger via useEffect when itemData + editableData.quantity changes)
          } catch (createErr) {
            // Placement succeeded but creating next StcTrans failed
            console.error('Error creating next StcTrans for remaining quantity:', createErr);
            setError(`Plassering utført (${placementQty} → ${finalLocation.trim()}), men kunne ikke opprette ny transaksjon for gjenstående ${newRemaining}. Lukk og behandle gjenstående separat.`);
            setIsComplete(true);
          }
        }
      } else {
        setError(response.errorMessage || t("warehouse.modules.receipt.directReceipt.errors.processFailed"));
      }
    } catch (err) {
      console.error('Error processing direct receipt:', err);
      setError(t("warehouse.modules.receipt.directReceipt.errors.processError", { error: err instanceof Error ? err.message : t("common.errors.unknownError") }));
    } finally {
      setLoading(false);
      setIsProcessingSplit(false);
    }
  };

  const handleClose = () => {
    // If placements have been made but not complete, confirm before closing
    if (completedPlacements.length > 0 && !isComplete) {
      const confirmed = window.confirm(
        `Du har plassert ${totalPlaced} av ${originalTotalQuantity} ${currentStcTrans.itemUnitTxt}. ` +
        `Gjenstående ${remainingQuantity} ${currentStcTrans.itemUnitTxt} kan behandles senere.\n\n` +
        `Vil du lukke?`
      );
      if (!confirmed) return;
    }

    // Refresh parent if any placements were made (both complete and mid-split)
    if (completedPlacements.length > 0 && onSuccess) {
      onSuccess();
    }

    setError(null);
    setLocationSuggestions([]);
    setSelectedLocation('');
    setCustomLocation('');
    setUseCustomLocation(false);
    setItemData(null);
    setAvailableBins([]);
    setBinSearchTerm('');
    setEditableData({
      quantity: stcTrans.quantity || 0,
      itemBatchNo: stcTrans.itemBatchNo || '',
      itemBatchExpDate: stcTrans.itemBatchExpDate
        ? new Date(stcTrans.itemBatchExpDate).toISOString().split('T')[0]
        : ''
    });
    // Reset split state
    setCurrentStcTrans(stcTrans);
    setRemainingQuantity(stcTrans.quantity || 0);
    setTotalPlaced(0);
    setCompletedPlacements([]);
    setIsComplete(false);
    setIsProcessingSplit(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-white w-full h-full sm:rounded-lg sm:shadow-xl sm:w-full sm:max-w-4xl sm:max-h-[90vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="px-3 sm:px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
            <Package className="h-5 w-5 text-green-500 flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{t("warehouse.modules.receipt.directReceipt.title")}</h2>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                {t("common.fields.itemNumber")}: {currentStcTrans.itemNo} • {t("common.fields.quantity")}: {editableData.quantity} {currentStcTrans.itemUnitTxt}
                {completedPlacements.length > 0 && ` (${totalPlaced}/${originalTotalQuantity} plassert)`}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">
          {/* Completion Summary */}
          {isComplete ? (
            <div className="flex flex-col items-center justify-center py-6 sm:py-10 space-y-4">
              <div className="bg-green-100 rounded-full p-4">
                <CheckCircle className="h-12 w-12 text-green-500" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Direkte mottak fullført</h3>
              <p className="text-sm text-gray-600 text-center">
                {currentStcTrans.itemNo} — {currentStcTrans.description1 || ''}
              </p>

              {/* Summary table */}
              <div className="w-full max-w-md bg-gray-50 rounded-lg p-3 sm:p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Oppsummering av plasseringer</h4>
                <div className="space-y-2">
                  {completedPlacements.map((p, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm border-b border-gray-200 pb-1 last:border-0">
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span className="font-medium">{p.location}</span>
                      </div>
                      <span className="text-gray-700">{p.quantity} {currentStcTrans.itemUnitTxt}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-2 border-t border-gray-300 flex justify-between text-sm font-semibold">
                  <span>Totalt plassert</span>
                  <span className="text-green-700">{totalPlaced} {currentStcTrans.itemUnitTxt}</span>
                </div>
              </div>

              {/* Error message if any (e.g., failed to create next StcTrans) */}
              {error && (
                <div className="w-full max-w-md bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <span className="text-yellow-800 text-sm">{error}</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
          <>
          {/* StcTrans Info */}
          <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
            <h3 className="text-xs sm:text-sm font-medium text-gray-900 mb-2">{t("warehouse.modules.receipt.directReceipt.transactionInfo")}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 text-xs sm:text-sm">
              <div>
                <span className="text-gray-600">{t("common.fields.itemNumber")}:</span>
                <div className="font-medium">{currentStcTrans.itemNo}</div>
              </div>
              <div>
                <span className="text-gray-600">{t("common.fields.description")}:</span>
                <div className="font-medium">{currentStcTrans.description1 || t("common.fields.notSpecified")}</div>
              </div>
              <div>
                <span className="text-gray-600">{t("warehouse.general.location")}:</span>
                <div className="font-medium">{currentStcTrans.locationName || t("common.fields.notSpecified")}</div>
              </div>
              {currentStcTrans.itemSerialNo && (
                <div>
                  <span className="text-gray-600">Serienummer:</span>
                  <div className="font-medium font-mono text-purple-700">{currentStcTrans.itemSerialNo}</div>
                </div>
              )}
            </div>
          </div>

          {/* Split Progress Section - shown after first placement */}
          {completedPlacements.length > 0 && !isComplete && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3">
              <div className="flex items-center space-x-2 mb-2">
                <BarChart3 className="h-4 w-4 text-blue-600" />
                <h3 className="text-xs sm:text-sm font-medium text-blue-900">
                  Plassert {totalPlaced} av {originalTotalQuantity} {currentStcTrans.itemUnitTxt} — gjenstår {remainingQuantity}
                </h3>
              </div>
              {/* Progress bar */}
              <div className="w-full bg-blue-200 rounded-full h-2.5 mb-2">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${(totalPlaced / originalTotalQuantity) * 100}%` }}
                />
              </div>
              {/* Completed placements list */}
              <div className="space-y-1">
                {completedPlacements.map((p, idx) => (
                  <div key={idx} className="flex items-center space-x-2 text-xs sm:text-sm text-blue-800">
                    <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                    <span>{p.quantity} {currentStcTrans.itemUnitTxt} → {p.location}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Editable Transaction Data */}
          <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
            <h3 className="text-xs sm:text-sm font-medium text-gray-900 mb-2 sm:mb-3">{t("warehouse.modules.receipt.directReceipt.editableData")}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t("common.fields.quantity")}</label>
                <input
                  type="number"
                  value={editableData.quantity}
                  onChange={(e) => {
                    const value = Math.min(parseFloat(e.target.value) || 0, remainingQuantity);
                    setEditableData(prev => ({ ...prev, quantity: value }));
                  }}
                  className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  step="0.1"
                  min="0"
                  max={remainingQuantity}
                />
                <span className="text-xs text-gray-500">{currentStcTrans.itemUnitTxt}</span>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t("warehouse.modules.receipt.batchNumber")}
                  {(currentStcTrans.itemType === 1 || currentStcTrans.itemType === 5) && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                <input
                  type="text"
                  value={editableData.itemBatchNo}
                  onChange={(e) => setEditableData(prev => ({ ...prev, itemBatchNo: e.target.value }))}
                  className={`w-full min-h-[44px] px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    ((currentStcTrans.itemType === 1 || currentStcTrans.itemType === 5) && !editableData.itemBatchNo?.trim())
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300'
                  }`}
                  placeholder={currentStcTrans.itemType === 4 ? "Auto-genereres fra utløpsdato" : t("warehouse.modules.receipt.batchNumber")}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t("warehouse.modules.receipt.expiryDate")}
                  {currentStcTrans.itemType === 4 && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                <input
                  type="date"
                  value={editableData.itemBatchExpDate}
                  onChange={(e) => setEditableData(prev => ({ ...prev, itemBatchExpDate: e.target.value }))}
                  className={`w-full min-h-[44px] px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    (currentStcTrans.itemType === 4 && !editableData.itemBatchExpDate?.trim())
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300'
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Location Suggestions Loading */}
          {loadingLocations && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                <span className="text-blue-700 text-sm">{t("warehouse.modules.receipt.directReceipt.loadingLocationSuggestions")}</span>
              </div>
            </div>
          )}

          {/* Location Selection */}
          {!loadingLocations && locationSuggestions.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
              <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center space-x-2">
                <MapPin className="h-4 w-4" />
                <span>{t("warehouse.modules.receipt.directReceipt.suggestedLocations")}</span>
                <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">
                  {t("warehouse.modules.receipt.directReceipt.suggestionCount", { count: locationSuggestions.length })}
                </span>
              </h3>
              
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {locationSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className={`p-2 border rounded-lg cursor-pointer transition-colors ${
                      selectedLocation === suggestion.binCode && !useCustomLocation
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => {
                      setSelectedLocation(suggestion.binCode);
                      setUseCustomLocation(false);
                      // Auto-fill quantity with min of suggestion capacity and remaining
                      const autoQty = Math.min(suggestion.resQuantity, remainingQuantity);
                      setEditableData(prev => ({ ...prev, quantity: autoQty }));
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <MapPin className={`h-4 w-4 ${suggestion.dummy ? 'text-orange-500' : 'text-green-500'}`} />
                        <span className="font-medium text-gray-900">{suggestion.binCode}</span>
                        {suggestion.dummy && (
                          <span className="text-xs bg-orange-100 text-orange-600 px-1 py-0.5 rounded">
                            {t("warehouse.modules.receipt.directReceipt.dummy")}
                          </span>
                        )}
                        <span className="text-sm text-gray-600">
                          {t("warehouse.general.zone")}: {suggestion.stcZoneID || t("common.fields.notSpecified")}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {suggestion.resQuantity} {currentStcTrans.itemUnitTxt}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Custom Location */}
          <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
            <h3 className="text-sm font-medium text-gray-900 mb-2">{t("warehouse.modules.receipt.directReceipt.overrideLocation")}</h3>
            <div className="flex items-center space-x-3 mb-3">
              <input
                type="checkbox"
                id="useCustomLocation"
                checked={useCustomLocation}
                onChange={(e) => setUseCustomLocation(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="useCustomLocation" className="text-sm text-gray-700">
                {t("warehouse.modules.receipt.directReceipt.useCustomLocation")}
              </label>
            </div>
            {useCustomLocation && (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={binSearchTerm}
                    onChange={(e) => setBinSearchTerm(e.target.value)}
                    placeholder={t("warehouse.modules.receipt.directReceipt.searchLocationPlaceholder")}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                {loadingBins ? (
                  <div className="flex items-center space-x-2 py-2">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                    <span className="text-sm text-gray-600">{t("warehouse.modules.receipt.directReceipt.loadingLocations")}</span>
                  </div>
                ) : (
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded">
                    {availableBins
                      .filter(bin => 
                        bin.binCode.toLowerCase().includes(binSearchTerm.toLowerCase()) ||
                        (bin.description1 && bin.description1.toLowerCase().includes(binSearchTerm.toLowerCase()))
                      )
                      .map((bin) => (
                        <div
                          key={bin.id}
                          className={`p-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                            customLocation === bin.binCode ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => setCustomLocation(bin.binCode)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <MapPin className="h-4 w-4 text-gray-400" />
                              <span className="font-medium text-sm">{bin.binCode}</span>
                              {bin.description1 && (
                                <span className="text-xs text-gray-500">({bin.description1})</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">
                              {bin.stcZoneID || 'Ingen sone'}
                            </div>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-4 w-4 text-red-500" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Selected Location Summary */}
          {(selectedLocation || customLocation) && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <h3 className="text-sm font-medium text-green-900 mb-2">{t("warehouse.modules.receipt.directReceipt.selectedLocation")}</h3>
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-green-500" />
                <span className="font-medium text-green-900">
                  {useCustomLocation ? customLocation : selectedLocation}
                </span>
                <ArrowRight className="h-4 w-4 text-green-500" />
                <span className="text-green-700">
                  {editableData.quantity} {currentStcTrans.itemUnitTxt} av {currentStcTrans.itemNo}
                </span>
              </div>
            </div>
          )}
          </>
          )}
        </div>

        {/* Footer */}
        <div className="px-3 sm:px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-0 sm:justify-between flex-shrink-0">
          {isComplete ? (
            <>
              <div className="text-xs sm:text-sm text-green-600 text-center sm:text-left font-medium">
                Alle {originalTotalQuantity} {currentStcTrans.itemUnitTxt} er plassert
              </div>
              <button
                onClick={handleClose}
                className="min-h-[44px] px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
              >
                Lukk
              </button>
            </>
          ) : (
            <>
              <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                {loadingLocations ? (
                  <div className="flex items-center justify-center sm:justify-start space-x-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{t("warehouse.modules.receipt.directReceipt.loadingLocationSuggestions")}</span>
                  </div>
                ) : completedPlacements.length > 0 ? (
                  <span>Gjenstår {remainingQuantity} {currentStcTrans.itemUnitTxt} å plassere</span>
                ) : locationSuggestions.length > 0 ? (
                  <span>{t("warehouse.modules.receipt.directReceipt.locationSuggestionsLoaded", { count: locationSuggestions.length })}</span>
                ) : (
                  <span>{t("warehouse.modules.receipt.directReceipt.noLocationSuggestions")}</span>
                )}
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={handleClose}
                  className="flex-1 sm:flex-none min-h-[44px] px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  {t("common.buttons.cancel")}
                </button>
                <button
                  onClick={handleDirectReceipt}
                  disabled={loading || isProcessingSplit || (!selectedLocation && !customLocation) || isRequiredFieldsMissing()}
                  className={`flex-1 sm:flex-none flex items-center justify-center space-x-2 min-h-[44px] px-4 py-2 rounded-lg transition-colors font-medium ${
                    loading || isProcessingSplit || (!selectedLocation && !customLocation) || isRequiredFieldsMissing()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-500 text-white hover:bg-green-600'
                  }`}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>{t("common.status.processing")}</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      {completedPlacements.length > 0 ? (
                        <span>Plasser {editableData.quantity} {currentStcTrans.itemUnitTxt}</span>
                      ) : (
                        <>
                          <span className="hidden sm:inline">Utfør direkte mottak</span>
                          <span className="sm:hidden">Utfør mottak</span>
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
